// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import FormDialog from 'Ui/components/common/form-dialog/form-dialog';
import FormValidator from 'Ui/components/higher-order-components/form-validator';
import {closeDialog as closeReduxDialog} from 'Store/actions/dialogs';
import dialogTypes from 'Ui/components/common/dialog-types';
import {postInventoryTransfers} from 'Services/inventory-service';
import {useSave} from 'Ui/react-hooks/use-save';
import SortedMultiSelect from 'Ui/components/common/form/sorted-multi-select';
import {replaceTranslationNames} from 'Utils/translation-utils';
import ValidationMultiSelect from 'Ui/components/common/form/validation-multi-select';
import {addHierarchyOfMembershipsToList} from 'OnEquip/equipment/utils/membership-hierarchy-utils';
import SelectedPartQuantityFromStock from 'Ui/components/common/parts-selectors/selected-part-quantity-from-stock';
import PartSelector from 'Ui/components/common/parts-selectors/part-selector';
import InventoryPartsTotalCost from 'Ui/features/onequip/parts/inventory/inventory-parts-total-cost';
import {getInventoriesByMembershipId} from 'Services/membership-service';
import {asNumber} from 'Utils/conversion-utils';
import {Box} from '@deere/isg.component-library';

function TransferInventoryDialog(props) {
    const {
        availableParts,
        closeDialog,
        currencyPreference,
        initialSelectedPart,
        invalidInputs,
        inventories,
        isgToggles,
        membershipHierarchy,
        membership,
        onClose,
        setValid,
        translations
    } = props;

    const [selectedMembershipId, setSelectedMembershipId] = React.useState(null);
    const [selectedParts, setSelectedParts] = React.useState(initialSelectedPart ? [{
        ...initialSelectedPart,
        id: initialSelectedPart.partId,
        quantity: 0,
        title: initialSelectedPart.partNumber,
        subtitle: `${initialSelectedPart.manufacturerName} - ${initialSelectedPart.name}`
    }] : []);

    const [saveFunc, disableSave, isSaving] = useSave(async () => {
        const {inventoryIds: destinationsInventoryIds} = await getInventoriesByMembershipId(selectedMembershipId);

        const partTransfers = selectedParts.map((part) => ({
            ...part,
            quantity: asNumber(part.quantity),
            totalCost: asNumber(part.totalCost),
            unitCost: asNumber(part.unitCost)
        }));

        const transferData = {
            destinationInventoryId: destinationsInventoryIds[0],
            partTransfers,
            sourceInventoryId: inventories[0].inventoryId
        };

        await postInventoryTransfers(transferData);

        if (onClose) {
            onClose();
        }

        closeDialog();
    }, {
        invalidInputs
    });

    const allSelectableMemberships = React.useMemo(() => addHierarchyOfMembershipsToList(
        null,
        membershipHierarchy,
        membership.membershipId
    ), [membershipHierarchy]);

    const initializedAvailableParts = React.useMemo(() => availableParts.map((part) => ({
        ...part,
        quantity: 0
    })), [availableParts]);

    return (
        <FormDialog
            cancelLabel={translations.CANCEL}
            className='transfer-inventory-dialog dropdown-overflow-dialog'
            closeHandler={closeDialog}
            disableSave={disableSave || selectedParts.length === 0}
            footerLoading={isSaving}
            onSave={saveFunc}
            saveLabel={translations.ONLINK_APPLY_TRANSFER}
            title={translations.ONLINK_TRANSFER_INVENTORY}
            translations={translations}
            width={'md'}
        >
            <form className='transfer-inventory-form'>
                <Box
                    sx={{
                        marginBottom: '16px'
                    }}
                >
                    <ValidationMultiSelect
                        className='transfer-inventory-multiselect multiselect'
                        component={SortedMultiSelect}
                        defaultSelectLabel={translations.ONLINK_SELECT_MEMBERSHIP}
                        items={allSelectableMemberships}
                        label={`${replaceTranslationNames(translations.ONLINK_TRANSFER_FROM_TO, {
                            '0': membership.name
                        })}:`}
                        name='membershipSelect'
                        onChange={(selectedIds) => setSelectedMembershipId(selectedIds[0])}
                        selectedIds={[selectedMembershipId]}
                        setValid={setValid}
                        translations
                    />
                </Box>
                <PartSelector
                    additionalSubTitleSelector='partNumber'
                    availableParts={initializedAvailableParts}
                    currencyPreference={currencyPreference}
                    disabled={false}
                    inventories={inventories}
                    isgToggles={isgToggles}
                    partsInputComponent={SelectedPartQuantityFromStock}
                    selectedParts={selectedParts}
                    selectedPartsClassName={'selected-parts'}
                    setSelectedParts={setSelectedParts}
                    setValid={setValid}
                    showAddNewPartOption={false}
                    subTitleSelector='manufacturerName'
                    titleSelector='name'
                    translations={translations}
                />
                <InventoryPartsTotalCost
                    currencyPreference={currencyPreference}
                    isgToggles={isgToggles}
                    selectedParts={selectedParts}
                    translations={translations}
                />
            </form>
        </FormDialog>
    );
}

TransferInventoryDialog.propTypes = {
    availableParts: PropTypes.arrayOf(PropTypes.object),
    closeDialog: PropTypes.func,
    currencyPreference: PropTypes.string,
    initialSelectedPart: PropTypes.object,
    invalidInputs: PropTypes.instanceOf(Set),
    inventories: PropTypes.arrayOf(PropTypes.object),
    isgToggles: PropTypes.object,
    membership: PropTypes.membership,
    membershipHierarchy: PropTypes.object,
    onClose: PropTypes.func,
    setValid: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        membership: state.membership,
        translations: state.translations,
        currencyPreference: state.membership.currencyPreference,
        isgToggles: state.account.isgToggles
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeDialog() {
            dispatch(closeReduxDialog(dialogTypes.TRANSFER_INVENTORY_DIALOG));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(FormValidator(TransferInventoryDialog));
